import { t } from 'i18next';

import { CustomPromptCheckSettings } from '../../../../../gamma-api';

import { getCheckTypeTitle, getDetectorTypeTitle, getGuardrailBehaviorLabel } from './labels';
import {
  GuardrailCheckSettings,
  GuardrailDetectorSettings,
  IGuardrailSummaryItem,
  IGuardrailSummaryLineItem,
  ICheckStep,
  IMaskingDetectorStep,
  CheckTypeValue,
  MaskingDetectorTypeValue,
} from './types';

const getSummaryLineItem = (label: string, value: string): IGuardrailSummaryLineItem => ({
  kind: 'line',
  label,
  value,
});

export const getCheckSummaryItems = (step: ICheckStep): IGuardrailSummaryItem[] => {
  const check = step.check;
  const commonItems = [
    getSummaryLineItem(t('Agent: Guardrails: Step: Type: Title'), getCheckTypeTitle(check.type)),
    getSummaryLineItem(t('Agent: Guardrails: Step: Behavior: Title'), getGuardrailBehaviorLabel(check.behavior)),
  ];

  if (check.type === CheckTypeValue.ExternalCheck) {
    return [
      ...commonItems,
      getSummaryLineItem(
        t('Agent: Guardrails: Webhook: URL: Title'),
        check.api_url?.trim() || t('Agent: Guardrails: Steps: Not configured')
      ),
    ];
  }

  if (check.type === CheckTypeValue.RegexCheck) {
    return [
      ...commonItems,
      getSummaryLineItem(
        t('Agent: Guardrails: RegexCheck: Patterns: Title'),
        check.patterns?.length ? check.patterns.join(', ') : t('Agent: Guardrails: Steps: Not configured')
      ),
    ];
  }

  const prompt = (check as CustomPromptCheckSettings).prompt?.trim();

  return [
    ...commonItems,
    {
      kind: 'prompt',
      label: t('Agent: Guardrails: CustomPromptCheck: Prompt: Title'),
      value: prompt || t('Agent: Guardrails: Steps: Not configured'),
      configured: !!prompt,
    },
  ];
};

export const getDetectorSummaryItems = (step: IMaskingDetectorStep): IGuardrailSummaryItem[] => {
  const detector = step.detector;
  const commonItems = [
    getSummaryLineItem(t('Agent: Guardrails: Masking: Detector: Type'), getDetectorTypeTitle(detector.type)),
  ];

  if (detector.type === MaskingDetectorTypeValue.External) {
    return [
      ...commonItems,
      getSummaryLineItem(
        t('Agent: Guardrails: Webhook: URL: Title'),
        detector.api_url?.trim() || t('Agent: Guardrails: Steps: Not configured')
      ),
    ];
  }

  if (detector.type === MaskingDetectorTypeValue.Regex) {
    return [
      ...commonItems,
      getSummaryLineItem(
        t('Agent: Guardrails: RegexCheck: Patterns: Title'),
        detector.patterns?.length ? detector.patterns.join(', ') : t('Agent: Guardrails: Steps: Not configured')
      ),
    ];
  }

  return commonItems;
};

export const getDetectorDisplayName = (detector: GuardrailDetectorSettings): string =>
  detector.name?.trim() || getDetectorTypeTitle(detector.type);

export const getCheckDisplayName = (check: GuardrailCheckSettings): string =>
  check.name?.trim() || getCheckTypeTitle(check.type);
